/* VIP Cart Core (v160, 2026-11-01) – one shared, consistent view of the Shopify cart.
 *
 * Loaded FIRST (before vip-custom-pricing.js, vip-cart-drawer.js and vip-unique-cart-guard.js), so it is the
 * innermost fetch wrapper: every cart read and cart change made by any theme script passes through it.
 *
 * What it does (performance only – it never decides what may be added, removed or checked out):
 *  1. GET /cart.js is shared. Reads that happen at the same moment share one request, and a cart that was read
 *     (or returned by a cart change) less than FRESH_MS ago is reused instead of reading again.
 *  2. Any cart change (add / change / update / clear) immediately marks the shared cart out of date. Reads made
 *     while a change is in flight wait for that change and use the cart it returns, so nobody ever sees the
 *     cart from before the change.
 *  3. /cart/add.js requests with a JSON body ask Shopify to return the updated cart in the same response
 *     (Shopify "bundled section rendering": sections/vip-cart-json.liquid). /cart/change.js, update.js and
 *     clear.js already return the full cart. So after a change no extra /cart.js read is needed.
 *  4. read({fresh:true}) always goes to Shopify (used right before checkout and for Reserve).
 *  5. When the tab becomes visible again, or comes back from the back/forward cache, the shared cart is dropped,
 *     so changes made in another tab are always read fresh.
 * If anything here fails, callers simply get a normal network response, exactly as before v160.
 */
(function () {
  'use strict';
  if (window.VIPCartCore) return;
  var nf = window.fetch ? window.fetch.bind(window) : null;
  if (!nf || !window.Promise || typeof Response === 'undefined') return;

  var FRESH_MS = 2000;
  var SECTION = 'vip-cart-json';
  var cache = null, cacheAt = 0, ver = 0;
  var inflight = null, inflightVer = -1;
  var pending = null;                 // promise of the cart returned by the cart change in flight (or null)
  var listeners = [];
  var stats = { network: 0, shared: 0, fromChange: 0 };

  function path(u) { try { return new URL(u, location.href).pathname.replace(/\/+$/, ''); } catch (e) { return ''; } }
  function kindOf(u, m) {
    var p = path(u);
    if (m === 'GET' && /\/cart\.js$/.test(p)) return 'read';
    if (m !== 'POST') return null;
    if (/\/cart\/add(\.js)?$/.test(p)) return 'add';
    if (/\/cart\/(change|update|clear)(\.js)?$/.test(p)) return 'mutate';
    return null;
  }
  function copy(c) { return JSON.parse(JSON.stringify(c)); }
  function emit(c) { for (var i = 0; i < listeners.length; i++) { try { listeners[i](copy(c)); } catch (e) {} } }
  var lastToken = null;
  function setCart(c) { if (c && c.items) { if (c.token) lastToken = c.token; cache = c; cacheAt = Date.now(); emit(c); } }
  function synth(c) { return new Response(JSON.stringify(c), { status: 200, headers: { 'Content-Type': 'application/json; charset=utf-8' } }); }

  function netRead() {
    if (inflight && inflightVer === ver) return inflight;
    var v = ver;
    stats.network++;
    var p = nf('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) { var e = new Error('cart ' + r.status); e.status = r.status; throw e; } return r.json(); })
      .then(function (c) { if (inflight === p) inflight = null; if (v === ver) setCart(c); return c; },
            function (e) { if (inflight === p) inflight = null; throw e; });
    inflight = p; inflightVer = v;
    return p;
  }
  // opts.fresh: always ask Shopify (after any change in flight has finished)
  function read(opts) {
    var fresh = !!(opts && opts.fresh);
    if (pending) {
      return pending.then(function (c) {
        if (c && !fresh) { stats.fromChange++; return copy(c); }
        return netRead().then(copy);
      });
    }
    if (!fresh && cache && Date.now() - cacheAt < FRESH_MS) { stats.shared++; return Promise.resolve(copy(cache)); }
    return netRead().then(copy);
  }
  function parseSection(d) {
    try {
      var h = d && d.sections && d.sections[SECTION];
      if (!h) return null;
      var m = /<script[^>]*data-vip-cart[^>]*>([\s\S]*?)<\/script>/.exec(h);
      if (!m) return null;
      var c = JSON.parse(m[1]);
      if (!c || !c.items) return null;
      // Liquid's cart JSON omits a few /cart.js fields; fill them so every consumer sees the usual shape.
      if (!c.token && lastToken) c.token = lastToken;
      if (!c.discount_codes) c.discount_codes = [];
      c.items.forEach(function (it) {
        if (it.presentment_price == null && typeof it.price === 'number') it.presentment_price = it.price / 100;
        if (!it.quantity_rule) it.quantity_rule = { min: 1, max: null, increment: 1 };
        if (it.has_components == null) it.has_components = false;
      });
      return c;
    } catch (e) { return null; }
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    var k = kindOf(url, method);
    if (!k || typeof input !== 'string') return nf(input, init);

    if (k === 'read') {
      return read().then(synth, function () { return nf(input, init); });
    }

    // cart change
    ver++; cache = null;
    var myVer = ver, sectioned = false;
    if (k === 'add' && init && typeof init.body === 'string' && init.body.trim().charAt(0) === '{') {
      try {
        var j = JSON.parse(init.body);
        if (!j.sections) {
          j.sections = SECTION;
          var o = {}; for (var key in init) if (Object.prototype.hasOwnProperty.call(init, key)) o[key] = init[key];
          o.body = JSON.stringify(j);
          init = o; sectioned = true;
        }
      } catch (e) {}
    }
    var req = nf(input, init);
    var mine = req.then(function (res) {
      if (!res.ok) return null;
      return res.clone().json().then(function (d) {
        if (k === 'mutate') return d && d.items ? d : null;
        return sectioned ? parseSection(d) : null;
      }).catch(function () { return null; });
    }, function () { return null; }).then(function (c) {
      if (myVer === ver && c) { stats.fromChange++; setCart(c); }
      if (pending === mine) pending = null;
      return myVer === ver ? c : null;
    });
    pending = mine;
    return req;
  };

  // Other apps using XMLHttpRequest: any cart change makes the shared cart out of date.
  try {
    var XO = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, u) {
      try { var k = kindOf(String(u), String(m).toUpperCase()); if (k === 'add' || k === 'mutate') { ver++; cache = null; } } catch (e) {}
      return XO.apply(this, arguments);
    };
  } catch (e) {}
  // Another tab may have changed the cart while this one was hidden.
  function drop() { cache = null; ver++; }
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') drop(); });
  window.addEventListener('pageshow', function (e) { if (e.persisted) drop(); });

  window.VIPCartCore = {
    read: read,
    onCart: function (fn) { listeners.push(fn); if (cache) { try { fn(copy(cache)); } catch (e) {} } },
    peek: function () { return cache ? copy(cache) : null; },
    age: function () { return cache ? Date.now() - cacheAt : Infinity; },
    stats: stats,
    FRESH_MS: FRESH_MS
  };
})();
